import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { TractNRI } from 'components/nri-table/types';
import {
  excludeLtdsAtomFamily,
  showExpiredPermitsAtomFamily,
  excludeExpiredLtdAtomFamily,
  showExpiredPermitsFormationAtomFamily,
} from './atoms';

/**
 * Hook to manage the "Exclude LTDs" toggle state (INS-7119).
 * When ON: Subtracts LTD from Total, greys out LTD column, adjusts verification counts.
 * State is persisted to localStorage per tableId.
 * @param {string} tableId - Unique table identifier for localStorage key
 * @return {{excludeLtds: boolean, setExcludeLtds: Function}} The toggle state and setter
 */
export function useExcludeLtds(tableId: string) {
  const excludeLtdsAtom = useMemo(
    () => excludeLtdsAtomFamily(tableId),
    [tableId]
  );
  const [excludeLtds, setExcludeLtds] = useAtom(excludeLtdsAtom);

  return { excludeLtds, setExcludeLtds };
}

/**
 * Hook to manage the "Show Expired Permits" toggle state (INS-7119).
 * When ON: Shows Expired Permit column after Total column.
 * State is persisted to localStorage per tableId.
 * @param {string} tableId - Unique table identifier for localStorage key
 * @return {{showExpiredPermits: boolean, setShowExpiredPermits: Function}} The toggle state and setter
 */
export function useShowExpiredPermits(tableId: string) {
  const showExpiredPermitsAtom = useMemo(
    () => showExpiredPermitsAtomFamily(tableId),
    [tableId]
  );
  const [showExpiredPermits, setShowExpiredPermits] = useAtom(
    showExpiredPermitsAtom
  );

  return { showExpiredPermits, setShowExpiredPermits };
}

/**
 * Hook to manage the "Show Expired Permits" toggle state for Slot By Formation table (INS-7119).
 * When ON: Shows Expired Permit column after Total column.
 * State is persisted to localStorage per tableId.
 * @param {string} tableId - Unique table identifier for localStorage key
 * @return {{showExpiredPermits: boolean, setShowExpiredPermits: Function}} The toggle state and setter
 */
export function useShowExpiredPermitsFormation(tableId: string) {
  const showExpiredPermitsAtom = useMemo(
    () => showExpiredPermitsFormationAtomFamily(tableId),
    [tableId]
  );
  const [showExpiredPermits, setShowExpiredPermits] = useAtom(
    showExpiredPermitsAtom
  );

  return { showExpiredPermits, setShowExpiredPermits };
}

/**
 * Hook to manage the toggle state for excluding non-verifiable well statuses
 * (LTD and Expired Permit) and filter slots accordingly.
 * State is persisted to localStorage per tableId.
 * @deprecated Use useExcludeLtds and useShowExpiredPermits instead (INS-7119)
 * @param {TractNRI[]} slots - The slots to filter
 * @param {string} tableId - Unique table identifier for localStorage key
 * @return {{excludeExpiredLtd: boolean, setExcludeExpiredLtd: Function, filteredSlots: TractNRI[]}} The toggle state, setter, and filtered slots
 */
export function useExcludeExpiredLtd(slots: TractNRI[], tableId: string) {
  const excludeExpiredLtdAtom = useMemo(
    () => excludeExpiredLtdAtomFamily(tableId),
    [tableId]
  );
  const [excludeExpiredLtd, setExcludeExpiredLtd] = useAtom(
    excludeExpiredLtdAtom
  );

  const filteredSlots = useMemo(() => {
    if (!excludeExpiredLtd) return slots;
    // Both are compared against the STORED status: `LTD` is stored in caps (the one
    // member that isn't snake_case), `expired_permit` is not. The old display
    // spelling "Expired Permit" never matches now, which silently disabled this
    // half of the exclusion.
    return slots.filter(
      (slot) =>
        slot.wellStatus !== 'LTD' && slot.wellStatus !== 'expired_permit'
    );
  }, [slots, excludeExpiredLtd]);

  return { excludeExpiredLtd, setExcludeExpiredLtd, filteredSlots };
}
